import {createAction, createAsyncThunk} from '@reduxjs/toolkit';
import {systemDateFormat, withPayloadType} from '@lib/utils';
import {trackEvent, mapiqApi, ParkingReservationDto} from '@lib/infrastructure';
import {ParkingReservation, FeatureToggle, ParkingReservationCountPerNodeByDate} from '../../data/entities';
import {AppThunkApiConfig} from '../../createStore';
import {format} from 'date-fns';
import {getIsFeatureEnabled} from '../../infrastructure/features';
import parseISO from 'date-fns/parseISO';
import {handleError} from '../../infrastructure';
import {ValidationError, GenericError} from '../../infrastructure/errorHandler';
import {getInitialState} from './initialState';
import {ParkingReservationErrors} from './utils';
import {dismissParkingIntroduction} from '../preferences';
import {getParkingReservationsById} from './selectors';

const initialState: ParkingReservation = {
  id: '',
  date: '',
  nodeId: '',
  parkingLotId: '',
  zoneId: '',
  parkingLotName: '',
  zoneName: '',
};

export const cancelParkingReservation = createAction(
  'view/parkingReservation/cancelParkingReservation',
  withPayloadType<string>(),
);

export const loadParkingCount = createAsyncThunk<
  ParkingReservationCountPerNodeByDate,
  {selectedDate: string},
  AppThunkApiConfig
>('parkingReservations/loadParkingCount', async ({selectedDate: selectedDate}, {extra, getState}) => {
  if (!getIsFeatureEnabled(getState(), FeatureToggle.Parking)) {
    const {parkingCountPerNodeOnDate} = getInitialState();
    return parkingCountPerNodeOnDate;
  }

  const client = extra.apiClient;
  const api = mapiqApi(client);
  const selectedStringDate = parseISO(selectedDate);

  return await api.getParkingReservationCountPerNode(selectedStringDate, selectedStringDate);
});

export const loadParkingCountByDateRange = createAsyncThunk<
  ParkingReservationCountPerNodeByDate,
  {from: string; to: string},
  AppThunkApiConfig
>('parkingReservations/loadParkingCountByDateRange', async ({from, to}, {extra, getState}) => {
  if (!getIsFeatureEnabled(getState(), FeatureToggle.Parking)) {
    const {parkingCountPerNodeOnDate} = getInitialState();
    return parkingCountPerNodeOnDate;
  }

  const client = extra.apiClient;
  const api = mapiqApi(client);
  const fromDate = parseISO(from);
  const toDate = parseISO(to);

  return await api.getParkingReservationCountPerNode(fromDate, toDate);
});

export const loadMyParkingReservations = createAsyncThunk<
  ParkingReservation[],
  {from: string; to: string},
  AppThunkApiConfig
>(
  'parkingReservations/loadParkingReservations',
  async ({from: fromDateString, to: toDateString}, {extra, getState}) => {
    if (!getIsFeatureEnabled(getState(), FeatureToggle.Parking)) {
      return [];
    }

    const client = extra.apiClient;
    const api = mapiqApi(client);
    const from = parseISO(fromDateString);
    const to = parseISO(toDateString);

    return await api.getParkingReservations(from, to);
  },
);

export const reserveParkingSpot = createAsyncThunk<ParkingReservation, ParkingReservation, AppThunkApiConfig>(
  'parkingReservations/reserveParkingSpot',
  async ({date, nodeId}, {getState, extra, dispatch}) => {
    if (!getIsFeatureEnabled(getState(), FeatureToggle.Parking)) {
      return initialState;
    }
    const client = extra.apiClient;
    const api = mapiqApi(client);
    try {
      const data = {
        date: format(new Date(date), systemDateFormat),
        nodeId: nodeId,
      } as ParkingReservationDto;
      const response = await api.postParkingReservation(data);
      const userDismissedParkingIntroduction = getState().data.preferences.preferences.userDismissedParkingIntroduction;
      if (!userDismissedParkingIntroduction) {
        dispatch(dismissParkingIntroduction());
      }
      trackEvent('ReserveParkingSpot');
      dispatch(loadParkingCount({selectedDate: date}));
      return response;
    } catch (error) {
      const actualError = error as ValidationError;
      const {i18n} = extra;
      if (actualError.data && ParkingReservationErrors.ParkingLastSpotReservedError in actualError.data.errors) {
        dispatch(loadParkingCount({selectedDate: date})).unwrap();
        throw new GenericError(i18n.lookup('error:ParkingLastSpotReservedError'));
      } else {
        throw new GenericError(i18n.lookup('error:ParkingReservationFailed'));
      }
    }
  },
);
export const reserveParkingSpotWithPreferredLocation = createAsyncThunk<
  ParkingReservation,
  {selectedDate: string},
  AppThunkApiConfig
>(
  'parkingReservations/reserveParkingSpotWithPreferredLocation',
  async ({selectedDate}, {getState, extra, dispatch}) => {
    if (!getIsFeatureEnabled(getState(), FeatureToggle.Parking)) {
      return initialState;
    }
    const client = extra.apiClient;
    const api = mapiqApi(client);
    try {
      const data = {
        date: format(new Date(selectedDate), systemDateFormat),
      } as ParkingReservationDto;
      const response = await api.postParkingReservation(data);
      const userDismissedParkingIntroduction = getState().data.preferences.preferences.userDismissedParkingIntroduction;
      if (!userDismissedParkingIntroduction) {
        dispatch(dismissParkingIntroduction());
      }
      trackEvent('ReserveParkingSpot');
      dispatch(loadParkingCount({selectedDate}));
      return response;
    } catch (error) {
      const actualError = error as ValidationError;
      const {i18n} = extra;
      if (actualError.data && ParkingReservationErrors.ParkingLastSpotReservedError in actualError.data.errors) {
        dispatch(loadParkingCount({selectedDate})).unwrap();
        throw new GenericError(i18n.lookup('error:ParkingLastSpotReservedError'));
      } else {
        throw new GenericError(i18n.lookup('error:ParkingReservationFailed'));
      }
    }
  },
);

export const changeParkingSpot = createAsyncThunk<ParkingReservation, ParkingReservation, AppThunkApiConfig>(
  'parkingReservations/changeParkingSpot',
  async (parkingReservation, {getState, extra, dispatch, rejectWithValue}) => {
    if (!getIsFeatureEnabled(getState(), FeatureToggle.Parking)) {
      return initialState;
    }
    const client = extra.apiClient;
    const api = mapiqApi(client);
    const response = await api.patchParkingReservation(parkingReservation);
    const userDismissedParkingIntroduction = getState().data.preferences.preferences.userDismissedParkingIntroduction;
    if (!userDismissedParkingIntroduction) {
      dispatch(dismissParkingIntroduction());
    }
    trackEvent('EditParkingReservation');
    dispatch(loadParkingCount({selectedDate: parkingReservation.date}));
    return response;
  },
);

export const removeParkingSpot = createAsyncThunk<string, string, AppThunkApiConfig>(
  'parkingReservations/removeParkingSpot',
  async (parkingReservationId, {getState, extra, dispatch, rejectWithValue}) => {
    if (!getIsFeatureEnabled(getState(), FeatureToggle.Parking)) {
      return '';
    }

    const client = extra.apiClient;
    const api = mapiqApi(client);
    try {
      await api.deleteParkingReservation(parkingReservationId);
      trackEvent('DeleteParkingReservation');
      const {date} = getParkingReservationsById(getState(), parkingReservationId) ?? {};
      date?.length && dispatch(loadParkingCount({selectedDate: date}));
      return parkingReservationId;
    } catch (error) {
      handleError(error, dispatch);
      return rejectWithValue('Remove parking spot failed');
    }
  },
);
